/**
 *
 * App.js
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 *
 * NOTE: while this component should technically be a stateless functional
 * component (SFC), hot reloading does not currently support SFCs. If hot
 * reloading is not a necessity for you then you can refactor it and remove
 * the linting exception.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { Switch, Route, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import _ from 'lodash';
import Notifications from 'react-notification-system';
import { Dimmer, Header, Icon, Loader, Image } from 'semantic-ui-react';

import SidebarNav from 'components/Nav/Sidebar';
import TopNav from 'components/Nav/Top/Loadable';
import { store } from 'configureStore';
import { ACL, API, ROUTES } from 'shared/constants';
import aclService from 'shared/lib/acl';

import LogoImg from 'images/logoLogin.png';

import 'semantic-ui-css/semantic.min.css';
import 'react-select/dist/react-select.css';
import 'react-datepicker/dist/react-datepicker.css';

import { makeSelectAuth, makeSelectLocation } from './selectors';
import { revokeAuthAction, authAction } from '../Auth/actions';
import Auth from '../Auth/Loadable';
import Anagrafica from '../Anagrafica/Loadable';
import NotFoundPage from '../NotFoundPage/Loadable';
import AgenziaKiron from '../AgenziaKiron/Loadable';
import AgenziaTecnocasa from '../AgenziaTecnocasa/Loadable';
import Pratiche from '../Pratiche/Loadable';
import Pratica from '../Pratica/Loadable';
import Istituto from '../Istituto/Loadable';
import Prodotti from '../Prodotti/Loadable';
import Agenda from '../Agenda/Loadable';
import Commissioning from '../Commissioning/Loadable';
import Importazione from '../Importazione/Loadable';
import Modulistica from '../Modulistica/Loadable';
import Unsubscribe from '../Unsubscribe/Loadable';
import LandingPage from '../LandingPage/Loadable';
import Statistiche from '../Statistic/Loadable';
import Dashboard from '../Dashboard/Loadable';
import DashboardCommissioning from '../DashboardCommissioning/Loadable';
import BloccoProvvigioni from '../BloccoProvvigioni/Loadable';
import FatturaProvvigioneDownload from '../FatturaProvvigioneDownload/Loadable';
import Tickets from '../Tickets/Loadable';
import Reports from '../Reports/Loadable';
import Download from '../Download/Loadable';
import CandidatesArea from '../CandidatesArea/Loadable';
import Candidate from '../Candidate/Loadable';
import NeoArea from '../NeoArea/Lodable';
import NeoProfile from '../NeoProfile/Lodable';
import RecessoDetail from '../RecessoDetail/Loadable';
import NetworkManagment from '../NetworkManagment';
import EditorAvvisi from '../EditorAvvisi';
import ProcessoImpresaList from '../ProcessoImpresaList';
import ProcessoImpresaDetails from '../ProcessoImpresaDetails';
import ReteTutorDetail from '../ReteTutorDetail/Loadable';
import UpdateReteTutorDetail from '../UpdateReteTutorDetail/Loadable';
import Comediazione from '../Comediazione/Lodable';
import ControlsSector from '../ControlsSector/Loadable';

/** Component for default Helmet */
const DefaultHelmet = () => (
  <Helmet
    titleTemplate="%s - Kiron Partner - Leader nel settore della Mediazione Creditizia in Italia"
    defaultTitle="Kiron Partner - Leader nel settore della Mediazione Creditizia in Italia"
  >
    <meta
      name="description"
      content="Kiron Partner società di mediazione creditizia con esperienza ventennale, offre un servizio di consulenza completo e personalizzato per mutui, finanziamenti, prodotti assicurativi"
    />
  </Helmet>
);

class App extends React.Component {
  constructor(props) {
    super(props);

    this.state = {};
  }

  componentDidMount() {
    const sessionString = localStorage.getItem('session');

    if (sessionString) {
      store.dispatch(authAction(JSON.parse(sessionString)));

      return;
    }

    // Avoid to open the popup if we are on unsubscribe page
    if (window.location.pathname.startsWith('/unsubscribe/')) {
      return;
    }

    const width = 500;

    // Definition of popup page postion (bottom-right)
    const left = window.screenX + (window.outerWidth - width);
    const top = window.screenX + (window.outerWidth - width);

    // Open popup and focus on it
    const popupPage = window.open(
      API.BASEURL + API.ACCESS_TOKEN,
      'LOGIN',
      `width=${width},height=${700},left=${left},top=${top}`
    );

    if (!popupPage) {
      // eslint-disable-next-line no-alert
      alert('Disablita il blocco popup e ricarica la pagina per accedere a Campus');
      return;
    }

    popupPage.focus();

    // Add window event listener for intercept the data returned from hybridauth service
    window.addEventListener(
      'message',
      (event) => {
        // Get data from event
        const content = event.data;

        // If there is an error revoke session
        if (content.title === 'Internal Server Error') {
          store.dispatch(revokeAuthAction());
        }

        // User can log into Campus
        if (content.token_type === 'Bearer') {
          const accessToken = content.tokenId;
          const refreshToken = content.refresh_token;
          const user = content.username;

          if (accessToken && user && refreshToken) {
            const session = {
              user,
              accessToken: `${user}_${accessToken}`,
              refreshToken,
            };

            store.dispatch(authAction(session));
          }
        }

        try {
          window.removeEventListener('message', () => popupPage.close());
        } catch (error) {
          // console.error(error);
        }
      },
      false
    );
  }

  getCustomAuth = () => {
    const auth = this.props.auth;
    if (_.get(this.props.auth, 'session.user') && _.get(this.props.auth, 'ACL.list')) {
      auth.ACL.list = [_.get(this.props.auth, 'session.user')].concat(auth.ACL.list);
    }
    return auth;
  };

  addNotification = (event) => {
    event.preventDefault();
    this.notificationSystem.addNotification({
      message: 'Notification message',
      level: 'success',
    });
  };

  notificationWrapper = () => ({
    addNotification: (options) => {
      this.notificationSystem.addNotification({
        ...options,
        autoDismiss: options.level === 'success' ? 6 : 0,
      });
    },
  });

  menuItems = () => {
    const items = [];
    const isProductManagerImprese = aclService.check(this.props.auth, ['productManagerImprese']);

    items.push({
      name: 'Dashboard',
      icon: 'dashboard',
      route:
        aclService.check(this.getCustomAuth(), ACL.CAN_VIEW_SECTION_DASHBOARD) &&
        !aclService.check(this.getCustomAuth(), ACL.CANNOT_VIEW_DASHBOARD)
          ? '/dashboard/main'
          : '/',
    });

    items.push({ name: 'Anagrafiche', icon: 'id card', route: '/anagrafica/' });

    if (!isProductManagerImprese) {
      items.push({ name: 'Pratiche', icon: 'file text', route: '/pratiche/' });
    }

    items.push({ name: 'Imprese', icon: 'building', route: '/processo-imprese/' });

    if (!isProductManagerImprese) {
      items.push({
        name: 'Co-mediazioni Tutor',
        icon: 'graduation',
        route: '/co-mediazioni/comediazioneTutor',
        render: <span>Co-mediazioni</span>,
      });
    }

    if (aclService.check(this.props.auth, ACL.CAN_VIEW_SECTION_AGENZIE_KIRON)) {
      items.push({ name: 'Agenzie Kiron', icon: 'university', route: '/agenziakiron/' });
    }

    if (aclService.check(this.props.auth, ACL.CAN_VIEW_SECTION_AGENZIE_TECNOCASA)) {
      items.push({ name: 'Agenzie Tecnocasa', icon: 'university', route: '/agenziatecnocasa/' });
    }

    if (aclService.check(this.props.auth, ACL.CAN_VIEW_SECTION_ISTITUTI)) {
      items.push({ name: 'Istituti', icon: 'credit card alternative', route: '/istituto/' });
    }

    if (aclService.check(this.props.auth, ACL.CAN_VIEW_SECTION_PRODOTTI)) {
      items.push({ name: 'Prodotti', icon: 'shop', route: '/prodotti/' });
    }

    items.push({ name: 'Agenda', icon: 'calendar', route: '/agenda/' });

    if (aclService.check(this.props.auth, ACL.CAN_VIEW_SECTION_COMMISSIONING)) {
      items.push(
        { name: 'Dashboard Commissioning', icon: 'chart bar', route: '/dashboard-commissioning/main' },
        { name: 'Commissioning', icon: 'calculator', route: '/commissioning', extra: '/regole' },
        { name: 'Blocco Provvigioni', icon: 'lock', route: '/blocco-provvigioni' }
      );
    }

    items.push({ name: 'Modulistica', icon: 'file pdf outline', route: '/modulistica/' });

    if (aclService.check(this.props.auth, ACL.CAN_VIEW_SECTION_CANDIDATI)) {
      items.push({ name: 'Area candidati', icon: 'users', route: ROUTES.CANDIDATES_LIST });
    }

    if (aclService.check(this.props.auth, ACL.CAN_VIEW_SECTION_NEO_AREA)) {
      items.push({ name: 'Area Neo', icon: 'user plus', route: '/area-neo/profili' });
    }

    if (aclService.check(this.props.auth, ACL.CAN_VIEW_SECTION_GESTIONE_RETE)) {
      items.push({ name: 'Gestione Rete', icon: 'sitemap', route: '/gestione-rete/dashboard' });
    }

    if (aclService.check(this.props.auth, ACL.CAN_VIEW_REPORT)) {
      items.push({ name: 'Reports', icon: 'line graph', route: '/reports/' });
    }

    items.push({ name: 'Tickets assistenza', icon: 'ticket', route: '/tickets/' });

    if (aclService.check(this.props.auth, ACL.CAN_VIEW_AVVISI)) {
      items.push({ name: 'Editor avvisi', icon: 'text cursor', route: '/editor-avvisi/' });
    }

    if (
      aclService.check({ ACL: { list: [_.get(this.props.auth, 'session.user')] } }, ACL.CAN_VIEW_SECTION_STATISTICHE)
    ) {
      items.push({ name: 'Statistiche', icon: 'area graph', route: '/statistiche/' });
    }

    if (aclService.check(this.props.auth, ACL.CAN_VIEW_SECTION_SETTORE_CONTROLLI)) {
      items.push({ name: 'Settore Controlli', icon: 'clipboard check', route: '/settore-controlli/main' });
    }

    return items;
  };

  render() {
    const { auth, logout } = this.props;

    if (window.location.pathname.startsWith('/unsubscribe/')) {
      return (
        <div>
          <DefaultHelmet />
          <Route exact path="/unsubscribe/:token" render={() => <Unsubscribe />} />
        </div>
      );
    }

    // User has to authenticate
    if (auth.isAuthenticated === undefined) {
      return (
        <div>
          <DefaultHelmet />
          <Dimmer page active inverted style={{ backgroundColor: 'white' }}>
            <div>
              <Image style={{ paddingBottom: '25em', display: 'unset', width: '500px' }} src={LogoImg} />
              <Loader size="huge" inverted>
                <div style={{ marginTop: '0.5rem', color: 'black', fontWeight: 400, fontSize: 22 }}>
                  Autenticazione in corso...
                </div>
                <div style={{ marginTop: '0.5rem', color: 'black', fontWeight: 400, fontSize: 14 }}>
                  {"Si prega di autorizzare l'accesso."}
                </div>
              </Loader>
            </div>
          </Dimmer>
        </div>
      );
    }

    // User isn't authenticated, session expired
    if (auth.isAuthenticated === false) {
      return (
        <div>
          <DefaultHelmet />
          <Dimmer active page style={{ backgroundColor: 'rgba(0,0,0,200)' }}>
            <Header as="h2" icon inverted>
              <Icon name="warning" />
              La sessione è terminata.
              <Header.Subheader>
                Effettua nuovamente il login dalla piattaforma Intranet Gruppo Tecnocasa.
              </Header.Subheader>
            </Header>
          </Dimmer>
        </div>
      );
    }

    // User cannot access to Campus because it's blocked
    if (auth.campusAccessBlocked) {
      return (
        <div>
          <DefaultHelmet />
          <Dimmer active page style={{ backgroundColor: 'black' }}>
            <Header as="h2" icon inverted>
              <Icon name="dont" />
              Accesso negato.
              <Header.Subheader>
                <br />
                L’utente non è autorizzato ad accedere a Kiron Campus.
                <br />
                Per ulteriori informazioni, contatta l’assistenza.
              </Header.Subheader>
            </Header>
          </Dimmer>
        </div>
      );
    }

    // User not exists
    if (auth.userNotFound) {
      return (
        <div>
          <DefaultHelmet />
          <Dimmer active page style={{ backgroundColor: 'black' }}>
            <Header as="h2" icon inverted>
              <Icon name="user circle" />
              Utente non trovato.
              <Header.Subheader>
                <br />
                L’utente non è tra quelli censiti per l&apos; acesso a Kiron Campus.
                <br />
                Per ulteriori informazioni, contatta l’assistenza.
              </Header.Subheader>
            </Header>
          </Dimmer>
        </div>
      );
    }

    // User has a generic error
    if (auth.errorUser) {
      return (
        <div>
          <DefaultHelmet />
          <Dimmer active page style={{ backgroundColor: 'black' }}>
            <Header as="h2" icon inverted>
              <Icon name="user circle" />
              Si è verificato un errore con l&apos; utente corrente.
              <Header.Subheader>
                <br />
                Per ulteriori informazioni, contatta l’assistenza.
              </Header.Subheader>
            </Header>
          </Dimmer>
        </div>
      );
    }

    // User is authenticated but hasn't roles
    if (auth.isAuthenticated === true && !_.get(auth, 'ACL.list')) {
      return (
        <div>
          <DefaultHelmet />
          <div>
            <TopNav
              user={auth.isAuthenticated ? auth.session.user : ''}
              profile={_.get(auth, 'profiles.list') ? auth.profiles.list[0].tipo : '--'}
              onLogout={logout}
              notifications={this.notificationWrapper}
            />
          </div>
          <Dimmer page active inverted style={{ backgroundColor: 'white' }}>
            <div>
              <Image style={{ paddingBottom: '25em', display: 'unset', width: '500px' }} src={LogoImg} />
              <Loader size="huge" inverted>
                <div style={{ marginTop: '0.5rem', color: 'black', fontWeight: 400, fontSize: 22 }}>
                  Avvio applicativo in corso
                </div>
                <div style={{ marginTop: '0.5rem', color: 'black', fontWeight: 400, fontSize: 14 }}>
                  Si prega di attendere...
                </div>
              </Loader>
            </div>
          </Dimmer>
        </div>
      );
    }

    // User is authenticated and has roles
    return (
      <div>
        <DefaultHelmet />

        <div>
          <TopNav
            user={auth.isAuthenticated ? auth.session.user : ''}
            profile={_.get(auth, 'profiles.list') ? auth.profiles.list[0].tipo : '--'}
            onLogout={logout}
            notifications={this.notificationWrapper}
          />

          <SidebarNav
            visible
            items={this.menuItems()}
            style={{ transform: 'translate3D(0,0,0)', whiteSpace: 'pre-wrap' }}
          >
            <Switch>
              <Route
                exact
                path="/"
                render={(props) => (
                  <LandingPage
                    {...props}
                    mustRedirect={!aclService.check(this.props.auth, ACL.CANNOT_VIEW_DASHBOARD)}
                    notification={this.notificationWrapper}
                  />
                )}
              />
              <Route
                path="/dashboard/:tab"
                render={(props) => <Dashboard {...props} auth={auth} notification={this.notificationWrapper} />}
              />
              <Route
                exact
                path="/download/:id"
                render={(props) => <Download {...props} notification={this.notificationWrapper} />}
              />
              <Route
                exact
                path="/fattura-provvigione/:id"
                render={(props) => <FatturaProvvigioneDownload {...props} notification={this.notificationWrapper} />}
              />
              <Route
                exact
                path="/anagrafica"
                render={(props) => <Anagrafica {...props} notification={this.notificationWrapper} />}
              />
              <Route
                exact
                path="/pratiche"
                render={(props) => <Pratiche {...props} notification={this.notificationWrapper} />}
              />
              <Route
                path="/pratica/:id"
                render={(props) => <Pratica {...props} notification={this.notificationWrapper} />}
              />
              <Route
                exact
                path="/processo-imprese"
                render={(props) => (
                  <ProcessoImpresaList {...props} notification={this.notificationWrapper} auth={auth} />
                )}
              />
              <Route
                exact
                path="/processo-imprese/:id"
                render={(props) => (
                  <ProcessoImpresaDetails {...props} notification={this.notificationWrapper} auth={auth} />
                )}
              />
              <Route
                path="/istituto"
                render={(props) => <Istituto {...props} notification={this.notificationWrapper} />}
              />
              <Route
                exact
                path="/agenziakiron"
                render={(props) => <AgenziaKiron {...props} notification={this.notificationWrapper} />}
              />
              <Route
                exact
                path="/agenziatecnocasa"
                render={(props) => <AgenziaTecnocasa {...props} notification={this.notificationWrapper} />}
              />
              <Route
                exact
                path="/co-mediazioni/:tab"
                render={(props) => <Comediazione {...props} notification={this.notificationWrapper} auth={auth} />}
              />
              <Route
                exact
                path="/prodotti"
                render={(props) => <Prodotti {...props} notification={this.notificationWrapper} />}
              />
              <Route
                exact
                path="/agenda"
                render={(props) => <Agenda {...props} notification={this.notificationWrapper} />}
              />
              <Route
                path="/commissioning"
                render={(props) => <Commissioning {...props} notification={this.notificationWrapper} />}
              />
              <Route
                exact
                path="/importazione/:id"
                render={(props) => <Importazione {...props} notification={this.notificationWrapper} />}
              />
              <Route
                exact
                path="/importazione/:id/dettaglio"
                render={(props) => <Importazione {...props} notification={this.notificationWrapper} />}
              />
              <Route
                exact
                path="/modulistica"
                render={(props) => <Modulistica {...props} notification={this.notificationWrapper} />}
              />
              <Route
                exact
                path="/reports"
                render={(props) => <Reports {...props} notification={this.notificationWrapper} />}
              />
              <Route
                exact
                path="/tickets"
                render={(props) => <Tickets {...props} notification={this.notificationWrapper} />}
              />
              <Route exact path="/statistiche" render={() => <Statistiche />} />
              <Route
                path="/dashboard-commissioning/:tab"
                render={(props) => (
                  <DashboardCommissioning {...props} notification={this.notificationWrapper} auth={auth} />
                )}
              />
              <Route
                exact
                path="/blocco-provvigioni"
                render={(props) => <BloccoProvvigioni {...props} notification={this.notificationWrapper} auth={auth} />}
              />
              <Route
                exact
                path="/area-candidati"
                render={(props) => <CandidatesArea {...props} notification={this.notificationWrapper} auth={auth} />}
              />
              <Route
                exact
                path="/candidato/:id"
                render={(props) => <Candidate {...props} notification={this.notificationWrapper} auth={auth} />}
              />
              <Route
                exact
                path="/area-neo/:tab"
                render={(props) => <NeoArea {...props} notification={this.notificationWrapper} auth={auth} />}
              />
              <Route
                exact
                path="/profilo-neo/:id"
                render={(props) => <NeoProfile {...props} notification={this.notificationWrapper} auth={auth} />}
              />
              <Route
                exact
                path="/gestione-rete/recessi/:id"
                render={(props) => <RecessoDetail {...props} notification={this.notificationWrapper} auth={auth} />}
              />
              <Route
                exact
                path="/gestione-rete/:tab"
                render={(props) => <NetworkManagment {...props} notification={this.notificationWrapper} auth={auth} />}
              />
              <Route
                exact
                path="/editor-avvisi"
                render={(props) => <EditorAvvisi {...props} notification={this.notificationWrapper} auth={auth} />}
              />
              <Route
                exact
                path="/gestione-rete/tutor/:id"
                render={(props) => <ReteTutorDetail {...props} notification={this.notificationWrapper} auth={auth} />}
              />
              <Route
                exact
                path="/gestione-rete/updateTutor/:id"
                render={(props) => (
                  <UpdateReteTutorDetail {...props} notification={this.notificationWrapper} auth={auth} />
                )}
              />
              <Route
                path="/settore-controlli/:tab"
                render={(props) => <ControlsSector {...props} notification={this.notificationWrapper} auth={auth} />}
              />
              <Route component={NotFoundPage} />
            </Switch>

            <Route exact path="/login3" component={Auth} />
          </SidebarNav>
          <Notifications
            ref={(c) => {
              this.notificationSystem = c;
            }}
            style={{ transform: 'translate3D(0,0,0)' }}
          />
        </div>
      </div>
    );
  }
}

App.propTypes = {
  auth: PropTypes.object,
  logout: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  auth: makeSelectAuth(),
  location: makeSelectLocation(),
});

function mapDispatchToProps(dispatch) {
  return {
    logout: () => dispatch(revokeAuthAction()),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default withRouter(compose(withConnect)(App));



// WEBPACK FOOTER //
// ./app/containers/App/index.js